import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Building, Factory, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageContent } from '../hooks/usePageContent';
import FRANCE_REGIONS from '../data/franceRegions';

const API = process.env.REACT_APP_BACKEND_URL;

const GROUPE_DEFAULTS = {
  hero_title: 'SOPREL Groupe',
  hero_subtitle: "Fabricant français d'éléments en béton précontraint depuis 1957, SOPREL Groupe accompagne les acteurs de la construction avec expertise et innovation.",
  history_label: 'Notre histoire',
  history_title: 'Depuis 1957',
  history_text1: "Depuis sa création, SOPREL Groupe s'est imposé comme un acteur majeur de la préfabrication en béton en France. Notre expertise dans la conception et la fabrication d'éléments en béton précontraint nous permet de répondre aux exigences les plus strictes de nos clients.",
  history_text2: "Notre engagement envers la qualité, l'innovation et le développement durable nous permet de construire des partenariats durables avec les plus grands acteurs de la construction française.",
  history_text3: "",
  history_image: 'https://customer-assets.emergentagent.com/job_soprel-build/artifacts/33h4cxon_dapwide.avif',
  locations_label: 'Nos implantations',
  locations_title: '',
  locations_subtitle: '',
  cta_title: "Rejoignez l'aventure SOPREL",
  cta_text: "Nous sommes toujours à la recherche de talents passionnés. Découvrez nos offres d'emploi.",
};

const IMAGES = {
  factory: "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/khhs2w33_SOPREL_V1_77130_0043.JPG",
  panelLogo: "https://customer-assets.emergentagent.com/job_d59d775d-5692-4c00-811c-246ab4499e3b/artifacts/gok7fvs1_panneau%20logo%20SOPREL.png",
  esternay: "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/9pbrtxs2_esternay.png",
  garenne: "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/dxp5q869_SOPREL_V1_27600_0055.JPG",
  marolles: "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/2wv6and5_image001.png",
};

const PageHeader = ({ c }) => {
  return (
    <section className="pt-32 pb-20 bg-[#0e1c3f]" data-testid="groupe-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">
            Le Groupe
          </p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-6">
            {c.hero_title}
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed">
            {c.hero_subtitle}
          </p>
        </div>
      </div>
    </section>
  );
};

const HistorySection = ({ c }) => {
  return (
    <section className="py-24 bg-white" data-testid="history-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">
              {c.history_label}
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight mb-6">
              {c.history_title}
            </h2>
            <div className="space-y-6 text-slate-600 leading-relaxed">
              <p>{c.history_text1}</p>
              <p>{c.history_text2}</p>
              {c.history_text3 && <p>{c.history_text3}</p>}
            </div>
          </div>
          <div className="relative">
            <img 
              src={c.history_image} 
              alt="Usine SOPREL" 
              className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
};

const AnimatedNumber = ({ target, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 2000;
          const start = performance.now();
          const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const ease = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(target * ease));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
};

const StatsSection = () => {
  const stats = [
    { target: 68, suffix: '', prefix: '+', label: "Années d'expérience" },
    { target: 3, suffix: '', prefix: '', label: 'Sites de production' },
    { target: 1, suffix: '', prefix: '', label: 'Brevet DAP WIDE' },
    { target: 0, suffix: '', prefix: '', label: 'Réalisations dans toute la France', isIcon: true },
  ];

  return (
    <section className="py-20 bg-slate-50" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-display font-bold text-4xl sm:text-5xl text-[#0078b7] mb-2">
                {stat.isIcon ? (
                  <span>France</span>
                ) : stat.label === 'Brevet DAP WIDE' ? (
                  <span>Brevet</span>
                ) : (
                  <AnimatedNumber target={stat.target} suffix={stat.suffix} prefix={stat.prefix} />
                )}
              </p>
              <p className="text-slate-600 font-medium">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Geo-precise SVG coordinates calculated via Lambert projection
// Reference: IDF center (319, 155) = Paris (48.86°N, 2.35°E)
// Scale: ~29 SVG units/° longitude, ~80 SVG units/° latitude
const mapLocFromEstab = (e) => ({
  id: e.city.toLowerCase().replace(/\s+/g, '-'),
  name: e.city === 'PARIS' ? 'Paris 19e' : e.city === 'SAINT PIERRE LA GARENNE' ? 'St Pierre La Garenne' : e.city === 'ESTERNAY' ? 'Esternay' : e.city === 'MAROLLES SUR SEINE' ? 'Marolles sur Seine' : e.name,
  fullName: e.type === 'headquarters' ? 'Siège Social' : 'Site de Production',
  type: e.type,
  address: e.address,
  city: e.city,
  postal_code: e.postal_code,
  description: e.description || '',
  image: e.image_url || null,
  mapX: e.mapX || 300, mapY: e.mapY || 170,
});

const FALLBACK_LOCATIONS = [
  { id: 'paris', name: 'Paris 19e', fullName: 'Siège Social', type: 'headquarters', address: '32 rue de Cambrai', city: 'PARIS', postal_code: '75019', description: 'Direction générale, bureau d\'études et services commerciaux', image: 'https://customer-assets.emergentagent.com/job_soprel-build/artifacts/z9a22xpc_image001.png', mapX: 320, mapY: 152 },
  { id: 'saint-pierre-la-garenne', name: 'St Pierre La Garenne', fullName: 'Site de Production', type: 'production', address: '58 rue du Fond du Val', city: 'SAINT PIERRE LA GARENNE', postal_code: '27600', description: 'Production de dalles alvéolaires et prédalles', image: IMAGES.garenne, mapX: 291, mapY: 135 },
  { id: 'esternay', name: 'Esternay', fullName: 'Site de Production', type: 'production', address: 'ZI - rue des Noues', city: 'ESTERNAY', postal_code: '51130', description: 'Production de poutres et éléments précontraints', image: IMAGES.esternay, mapX: 354, mapY: 165 },
  { id: 'marolles-sur-seine', name: 'Marolles sur Seine', fullName: 'Site de Production', type: 'production', address: '92 route de Bray', city: 'MAROLLES SUR SEINE', postal_code: '77130', description: 'Production de dalles alvéolaires et DAP Wide', image: IMAGES.marolles, mapX: 338, mapY: 193 },
];

const FranceMapSVG = ({ activeIndex, onSelect, locations }) => {
  const [isVisible, setIsVisible] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (mapRef.current) observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={mapRef} className="relative w-full max-w-xl mx-auto">
      <svg
        viewBox={FRANCE_REGIONS.viewBox}
        className={`w-full h-auto transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        data-testid="france-map-svg"
      >
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="softGlow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="mapShadow">
            <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#000" floodOpacity="0.3"/>
          </filter>
        </defs>

        {/* France regions */}
        <g filter="url(#mapShadow)">
          {FRANCE_REGIONS.locations.map((region) => {
            const isIDF = region.id === 'idf';
            const isNormandie = region.id === 'nor';
            const isGrandEst = region.id === 'ges';
            const isHDF = region.id === 'hdf';
            const isCVL = region.id === 'cvl';
            const isBFC = region.id === 'bfc';
            const isPDL = region.id === 'pdl';
            const hasOurSite = isIDF || isNormandie || isGrandEst || isHDF || isCVL || isBFC || isPDL;
            return (
              <path
                key={region.id}
                d={region.path}
                fill={hasOurSite ? '#8a9fb3' : '#3d5568'}
                stroke="#1a2d45"
                strokeWidth="1"
                className="transition-all duration-500"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transitionDelay: `${Math.random() * 0.5}s`,
                }}
              />
            );
          })}
        </g>

        {/* Connection lines from Paris to production sites */}
        {isVisible && locations.slice(1).map((loc, i) => (
          <g key={`line-${loc.id}`}>
            <line
              x1={locations[0].mapX}
              y1={locations[0].mapY}
              x2={loc.mapX}
              y2={loc.mapY}
              stroke={activeIndex === i + 1 ? '#ff65c3' : '#0078b7'}
              strokeWidth={activeIndex === i + 1 ? 2 : 1}
              strokeOpacity={activeIndex === i + 1 ? 0.9 : 0.35}
              strokeDasharray="6 3"
              className="map-line-animate"
              style={{ animationDelay: `${(i + 1) * 0.3}s` }}
            />
          </g>
        ))}

        {/* Location markers */}
        {locations.map((loc, index) => {
          const isActive = activeIndex === index;
          const isHQ = loc.type === 'headquarters';
          const color = isHQ ? '#0078b7' : '#ff65c3';
          const r = isActive ? 8 : 5;

          return (
            <g
              key={loc.id}
              onClick={() => onSelect(index)}
              className="cursor-pointer"
              style={{
                opacity: isVisible ? 1 : 0,
                transition: `opacity 0.5s ease ${index * 0.2 + 0.8}s`
              }}
            >
              {/* Ping animation */}
              {isActive && (
                <circle cx={loc.mapX} cy={loc.mapY} r="5" fill="none" stroke={color} strokeWidth="1.5" opacity="0.6">
                  <animate attributeName="r" from="8" to="30" dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.6" to="0" dur="1.5s" repeatCount="indefinite" />
                </circle>
              )}

              {/* Outer glow */}
              <circle cx={loc.mapX} cy={loc.mapY} r={r * 1.8} fill={color} opacity={isActive ? 0.15 : 0.08} filter="url(#glow)" />

              {/* Main dot */}
              <circle
                cx={loc.mapX} cy={loc.mapY} r={r}
                fill={isActive ? color : '#1a2d5a'}
                stroke={color}
                strokeWidth={isActive ? 2 : 1.5}
                filter="url(#softGlow)"
                className="transition-all duration-300"
              />

              {/* Inner dot */}
              <circle cx={loc.mapX} cy={loc.mapY} r={isActive ? 3 : 2} fill="white" opacity={isActive ? 1 : 0.9} />

              {/* Label with background */}
              <g>
                <rect
                  x={loc.mapX + (index === 1 ? -130 : 15)}
                  y={loc.mapY - 12}
                  width={loc.name.length * 7.5 + 10}
                  height="22"
                  rx="3"
                  fill={isActive ? color : 'rgba(14,28,63,0.85)'}
                  opacity={isActive ? 0.95 : 0.75}
                />
                <text
                  x={loc.mapX + (index === 1 ? -125 : 20)}
                  y={loc.mapY + 3}
                  fill="white"
                  fontSize="11"
                  fontWeight={isActive ? '700' : '500'}
                  className="transition-all duration-300"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {loc.name}
                </text>
              </g>

              {/* Type badge below label */}
              {isActive && (
                <text
                  x={loc.mapX + (index === 1 ? -125 : 20)}
                  y={loc.mapY + 20}
                  fill={color}
                  fontSize="8"
                  fontWeight="700"
                  opacity="0.9"
                  style={{ fontFamily: 'Outfit, sans-serif', textTransform: 'uppercase', letterSpacing: '0.15em' }}
                >
                  {isHQ ? 'Siège social' : 'Pôle production'}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const EstablishmentsSection = ({ locations, c }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeLoc = locations[activeIndex];

  return (
    <section className="py-24 bg-[#0e1c3f] relative overflow-hidden" data-testid="establishments-section">
      {/* Background effects */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#0078b7] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#ff65c3] rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4">
            {c.locations_label}
          </h2>
          {c.locations_title && (
            <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">
              {c.locations_title}
            </p>
          )}
          {c.locations_subtitle && (
            <p className="text-slate-400 max-w-2xl mx-auto">
              {c.locations_subtitle}
            </p>
          )}
        </div>

        {/* Map + Detail layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* France Map */}
          <FranceMapSVG activeIndex={activeIndex} onSelect={setActiveIndex} locations={locations} />

          {/* Active location detail */}
          <div className="space-y-6">
            <div
              key={activeLoc.id}
              className="animate-float-up"
            >
              {/* Photo - portrait for production, no image for HQ */}
              {activeLoc.image ? (
                <div className="relative aspect-[3/4] max-h-[400px] overflow-hidden mb-6 rounded-sm">
                  <img
                    src={activeLoc.image}
                    alt={activeLoc.name}
                    className="w-full h-full object-cover"
                    data-testid={`location-image-${activeLoc.id}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e1c3f]/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block px-3 py-1 bg-[#ff65c3] text-white text-xs font-bold uppercase tracking-wider rounded-sm">
                      {activeLoc.type === 'headquarters' ? 'Siège Social' : 'Pôle Production'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="relative mb-6 p-6 rounded-sm bg-[#0078b7]/10 flex items-center gap-4">
                  <Building className="w-10 h-10 text-[#0078b7] flex-shrink-0" />
                  <div>
                    <span className="inline-block px-3 py-1 bg-[#0078b7] text-white text-xs font-bold uppercase tracking-wider rounded-sm">
                      {activeLoc.type === 'headquarters' ? 'Siège Social' : 'Pôle Production'}
                    </span>
                  </div>
                </div>
              )}

              {/* Details */}
              <div>
                <h3 className="font-heading font-bold text-2xl text-white mb-2">
                  {activeLoc.fullName} — {activeLoc.name}
                </h3>
                <p className="text-slate-400 mb-4">
                  {activeLoc.description}
                </p>
                <div className="flex items-start gap-2 text-slate-300">
                  <MapPin className="h-4 w-4 mt-1 flex-shrink-0 text-[#0078b7]" />
                  <p className="text-sm">
                    {activeLoc.address}<br />
                    {activeLoc.postal_code} {activeLoc.city}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom navigation tabs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {locations.map((loc, index) => {
            const isActive = activeIndex === index;
            const isHQ = loc.type === 'headquarters';
            return (
              <button
                key={loc.id}
                onClick={() => setActiveIndex(index)}
                className={`relative p-5 text-left transition-all duration-300 border rounded-sm group ${
                  isActive
                    ? 'bg-white/10 border-[#0078b7] backdrop-blur-sm'
                    : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:border-white/20'
                }`}
                data-testid={`location-tab-${loc.id}`}
              >
                {isActive && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#0078b7] to-[#ff65c3]" />
                )}
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${
                    isActive
                      ? (isHQ ? 'bg-[#0078b7]' : 'bg-[#ff65c3]')
                      : 'bg-white/10'
                  }`}>
                    {isHQ
                      ? <Building className={`h-4 w-4 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                      : <Factory className={`h-4 w-4 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                    }
                  </div>
                  <span className={`text-xs font-bold uppercase tracking-wider ${
                    isActive ? (isHQ ? 'text-[#0078b7]' : 'text-[#ff65c3]') : 'text-slate-500'
                  }`}>
                    {isHQ ? 'Siège' : 'Production'}
                  </span>
                </div>
                <h4 className={`font-heading font-bold text-sm transition-colors ${
                  isActive ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                }`}>
                  {loc.name}
                </h4>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const CTASection = ({ c }) => {
  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#0e1c3f] p-8 sm:p-12 lg:p-16 text-center">
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white uppercase tracking-tight mb-6">
            {c.cta_title}
          </h2>
          <p className="text-slate-300 text-base sm:text-lg mb-8 sm:mb-10 max-w-2xl mx-auto">
            {c.cta_text}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-[#0078b7] hover:bg-cyan-600 text-white font-medium px-8 rounded-sm"
            >
              <Link to="/recrutement">Nos offres d'emploi</Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 rounded-sm"
            >
              <Link to="/contact">Nous contacter</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const GroupePage = () => {
  const [locations, setLocations] = useState(FALLBACK_LOCATIONS);
  const { content: c } = usePageContent('groupe', GROUPE_DEFAULTS);

  useEffect(() => {
    const fetchEstablishments = async () => {
      try {
        const { data } = await axios.get(`${API}/api/establishments`);
        if (data && data.length > 0) {
          setLocations(data.map(mapLocFromEstab));
        }
      } catch (err) {}
    };
    fetchEstablishments();
  }, []);

  return (
    <div data-testid="groupe-page">
      <PageHeader c={c} />
      <HistorySection c={c} />
      <EstablishmentsSection locations={locations} c={c} />
      <CTASection c={c} />
    </div>
  );
};

export default GroupePage;
